import type { InventoryListByIdAPI } from '@openx/types/inventoryList';
import {
  type InstanceProps,
  type InventoryAndContentState,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import type { JSX } from 'react';
import type { AnyAction } from 'redux';
import type { InvalidItemsError } from '../../shared/BulkAdd/types';
import type { SetInventoryListItemsDispatch } from '../InventoryAndContentTopBox/constants';
import { AdunitItems } from './AdunitItems';
import { AppBundleId } from './AppBundleId';
import { AppInventoryItem } from './AppInventoryItem';
import { CustomBlockingRulesItems } from './CustomBlockingRulesItems';
import { DefaultItems } from './DefaultItems';
import { InventoryIDs } from './InventoryIDs';
import { InventoryItems } from './InventoryItems';
import { KeywordItems } from './KeywordItems';

type InventoryContentWithoutContentOperator = Exclude<
  InventoryContentOption,
  typeof InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR | typeof InventoryContentOption.APP_INVENTORY
>;

type InventoryContentWithoutAppInventory = Exclude<
  InventoryContentWithoutContentOperator,
  typeof InventoryContentOption.APP_GROUP | typeof InventoryContentOption.APP_BUNDLE_ID
>;

type InventoryAndContentT = {
  [K in InventoryContentWithoutAppInventory]: (
    isNotLastItem: boolean,
    showDimensionChipLine: boolean
  ) => JSX.Element | null;
} & {
  [InventoryContentOption.APP_GROUP]?: (isNotLastItem: boolean, showDimensionChipLine: boolean) => JSX.Element | null;
  [InventoryContentOption.APP_BUNDLE_ID]?: (
    isNotLastItem: boolean,
    showDimensionChipLine: boolean
  ) => JSX.Element | null;
};

export type GetInventoryDisabledReasonMessage = (option: InventoryContentOption) => string | undefined;

type GetInventoryAndContentOptionsArgs = {
  dispatchInventory: (action: AnyAction) => void;
  readonly?: boolean;
  targetingParams: InventoryAndContentState;
  accountUid: string;
  appBundleIdDuplicateErrors: InvalidItemsError | null;
  setInventoryListItems: SetInventoryListItemsDispatch;
  inventoryListItems: InventoryListByIdAPI['values'] | undefined;
  switchState: boolean;
  instanceState?: string;
  inputInstanceValue?: InstanceProps | null;
  isBefInstance: boolean;
  getDisabledReasonMessage?: GetInventoryDisabledReasonMessage;
};

export const getInventoryAndContentOptions = ({
  dispatchInventory,
  readonly,
  targetingParams,
  accountUid,
  appBundleIdDuplicateErrors,
  setInventoryListItems,
  inventoryListItems,
  switchState,
  instanceState,
  inputInstanceValue,
  isBefInstance,
  getDisabledReasonMessage,
}: GetInventoryAndContentOptionsArgs): InventoryAndContentT => {
  const msg = getDisabledReasonMessage;
  return {
    [InventoryContentOption.ADUNIT]: (isNotLastItem, showDimensionChipLine) => (
      <InventoryIDs
        dispatchInventory={dispatchInventory}
        readonly={readonly}
        option={InventoryContentOption.ADUNIT}
        targetingParams={targetingParams}
        key={InventoryContentOption.ADUNIT}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        dataTest={'ad-unit-ids'}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.ADUNIT)}
      />
    ),
    [InventoryContentOption.ADUNIT_SIZE]: (isNotLastItem, showDimensionChipLine) => (
      <AdunitItems
        dispatchInventory={dispatchInventory}
        readonly={readonly}
        targetingParams={targetingParams}
        key={InventoryContentOption.ADUNIT_SIZE}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        dataTest={'adunit-size'}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.ADUNIT_SIZE)}
      />
    ),
    [InventoryContentOption.APP_BUNDLE_ID]: (isNotLastItem, showDimensionChipLine) => (
      <AppBundleId
        readonly={readonly}
        targetingParams={targetingParams}
        dispatchInventory={dispatchInventory}
        key={InventoryContentOption.APP_BUNDLE_ID}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        accountUid={accountUid}
        appBundleIdDuplicateErrors={appBundleIdDuplicateErrors}
        setInventoryListItems={setInventoryListItems}
        inventoryListItems={inventoryListItems}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.APP_BUNDLE_ID)}
      />
    ),
    [InventoryContentOption.INVENTORY]: (isNotLastItem, showDimensionChipLine) => (
      <InventoryItems
        readonly={readonly}
        dispatchInventory={dispatchInventory}
        targetingParams={targetingParams}
        instanceState={instanceState}
        key={InventoryContentOption.INVENTORY}
        isInventorySelected={!!inputInstanceValue}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.INVENTORY)}
      />
    ),
    [InventoryContentOption.PAGE_URL]: (isNotLastItem, showDimensionChipLine) => (
      <DefaultItems
        readonly={readonly}
        dispatchInventory={dispatchInventory}
        targetingParams={targetingParams}
        option={InventoryContentOption.PAGE_URL}
        key={InventoryContentOption.PAGE_URL}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        dataTest={'page-url'}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.PAGE_URL)}
      />
    ),
    [InventoryContentOption.PUBLISHER_ID]: (isNotLastItem, showDimensionChipLine) =>
      isBefInstance ? (
        <InventoryIDs
          dispatchInventory={dispatchInventory}
          readonly={readonly}
          option={InventoryContentOption.PUBLISHER_ID}
          targetingParams={targetingParams}
          key={InventoryContentOption.PUBLISHER_ID}
          isNotLastItem={isNotLastItem}
          switchState={switchState}
          dataTest={'publisher-ids'}
          showDimensionChipLine={showDimensionChipLine}
          disabledReasonMessage={msg?.(InventoryContentOption.PUBLISHER_ID)}
        />
      ) : null,
    [InventoryContentOption.INSTANCE_HASH]: (isNotLastItem, showDimensionChipLine) =>
      isBefInstance ? (
        <InventoryIDs
          dispatchInventory={dispatchInventory}
          readonly={readonly}
          option={InventoryContentOption.INSTANCE_HASH}
          targetingParams={targetingParams}
          key={InventoryContentOption.INSTANCE_HASH}
          isNotLastItem={isNotLastItem}
          switchState={switchState}
          dataTest={'instance-hash'}
          showDimensionChipLine={showDimensionChipLine}
          disabledReasonMessage={msg?.(InventoryContentOption.INSTANCE_HASH)}
        />
      ) : null,
    [InventoryContentOption.SITE]: (isNotLastItem, showDimensionChipLine) => (
      <InventoryIDs
        dispatchInventory={dispatchInventory}
        readonly={readonly}
        option={InventoryContentOption.SITE}
        targetingParams={targetingParams}
        key={InventoryContentOption.SITE}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        dataTest={'site-ids'}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.SITE)}
      />
    ),
    [InventoryContentOption.KEYWORDS]: (isNotLastItem, showDimensionChipLine) => (
      <KeywordItems
        dispatchInventory={dispatchInventory}
        readonly={readonly}
        targetingParams={targetingParams}
        key={InventoryContentOption.KEYWORDS}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.KEYWORDS)}
      />
    ),
    [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: (isNotLastItem, showDimensionChipLine) => (
      <InventoryIDs
        dispatchInventory={dispatchInventory}
        readonly={readonly}
        option={InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID}
        targetingParams={targetingParams}
        key={InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        dataTest={'line-items-app-bundle-ids'}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID)}
      />
    ),
    [InventoryContentOption.PUBLISHER_INVENTORY_RULES]: (isNotLastItem, showDimensionChipLine) => (
      <CustomBlockingRulesItems
        dispatchInventory={dispatchInventory}
        readonly={readonly}
        targetingParams={targetingParams[InventoryContentOption.PUBLISHER_INVENTORY_RULES]}
        key={InventoryContentOption.PUBLISHER_INVENTORY_RULES}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.PUBLISHER_INVENTORY_RULES)}
      />
    ),
    [InventoryContentOption.APP_GROUP]: (isNotLastItem, showDimensionChipLine) => (
      <AppInventoryItem
        dispatchInventory={dispatchInventory}
        readonly={readonly}
        targetingParams={targetingParams}
        key={InventoryContentOption.APP_GROUP}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        showDimensionChipLine={showDimensionChipLine}
        disabledReasonMessage={msg?.(InventoryContentOption.APP_GROUP)}
      />
    ),
  };
};
